import { Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import E12Spinner from './components/E12Spinner';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import AuthenticatedLayout from './layouts/AuthenticatedLayout';
import PublicLayout from './layouts/PublicLayout';
import { QuestionDetail } from './modules/Questions/QuestionDetail';

// Lazy load pages
const HomePage = lazy(() => import('./pages/HomePage'));
const SignInPage = lazy(() => import('./pages/sign-in'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Authenticated pages
const HomeworksPage = lazy(() => import('./pages/homeworks/HomeworksPage'));
const HomeworkDetailPage = lazy(
  () => import('./pages/homeworks/HomeworkDetailPage')
);
const LessonsPage = lazy(() => import('./pages/lessons/LessonsPage'));
const LessonDetailPage = lazy(() => import('./pages/lessons/LessonDetailPage'));
const LiveLessonsPage = lazy(
  () => import('./pages/live-lessons/LiveLessonsPage')
);
const LiveExamsPage = lazy(() => import('./pages/live-exams/LiveExamsPage'));
const LiveExamDetailPage = lazy(
  () => import('./pages/live-exams/LiveExamDetailPage')
);
const LiveExamResultsPage = lazy(
  () => import('./pages/live-exams/LiveExamResultsPage')
);
const PlansPage = lazy(() => import('./pages/plans/PlansPage'));
const TimelinePage = lazy(() => import('./pages/timeline/TimelinePage'));
const ExamResultsPage = lazy(
  () => import('./pages/exam-results/ExamResultsPage')
);
const EtudesPage = lazy(() => import('./pages/etudes/EtudesPage'));
const EtudesCreateRequestPage = lazy(
  () => import('./pages/etudes/EtudesCreateRequestPage')
);
const CollectiveEtudesPage = lazy(
  () => import('./pages/collective-etudes/CollectiveEtudesPage')
);
const AnnouncementsPage = lazy(
  () => import('./pages/announcements/AnnouncementsPage')
);
const CanteenPage = lazy(() => import('./pages/canteen/CanteenPage'));
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));
const StudentTrackingNotesPage = lazy(
  () => import('./pages/student-tracking-notes/StudentTrackingNotesPage')
);
const MyAttendancePage = lazy(
  () => import('./pages/my-attendance/MyAttendancePage')
);
const AcademicCalendarPage = lazy(
  () => import('./pages/academic-calendar/AcademicCalendarPage')
);

const MessagingPage = lazy(() => import('./pages/messaging/MessagingPage'));
const MessagingDetailPage = lazy(
  () => import('./pages/messaging/MessagingDetailPage')
);
const ParentOperationsPage = lazy(
  () => import('./pages/parent-operations/ParentOperationsPage')
);
const VogCreateRequestPage = lazy(
  () => import('./pages/parent-operations/VogCreateRequestPage')
);
const MealsPage = lazy(() => import('./pages/meals/MealsPage'));
const TahtaAppPage = lazy(() => import('./pages/tahta-app/TahtaAppPage'));
const TahtaAppRegisterPage = lazy(
  () => import('./pages/tahta-app/TahtaAppRegisterPage')
);
const TahtaAppQuestionsPage = lazy(
  () => import('./pages/tahta-app/TahtaAppQuestionsPage')
);
const TahtaAppQuestionDetailPage = lazy(
  () => import('./pages/tahta-app/TahtaAppQuestionDetailPage')
);
const WorkCalendarPage = lazy(
  () => import('./pages/work-calendar/WorkCalendarPage')
);
const WorkCalendarDetailPage = lazy(
  () => import('./pages/work-calendar/WorkCalendarDetailPage')
);
const QuestionsPage = lazy(() => import('./pages/questions/QuestionsPage'));
const ShortlinkPage = lazy(() => import('./pages/shortlinks/ShortlinkPage'));
const OkumanyaAppPage = lazy(
  () => import('./pages/okumanya-app/OkumanyaAppPage')
);
const PdevcoAppPage = lazy(() => import('./pages/pdevco-app/PdevcoAppPage'));
const OrganizationalWorkPage = lazy(
  () => import('./pages/organizational-work/OrganizationalWorkPage')
);

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <E12Spinner />
  </div>
);

export default function App() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicRoute />}>
          <Route element={<PublicLayout />}>
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/sl/:id" element={<ShortlinkPage />} />
          </Route>
        </Route>

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route element={<AuthenticatedLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/homeworks" element={<HomeworksPage />} />
            <Route
              path="/homeworks/:homework"
              element={<HomeworkDetailPage />}
            />
            <Route path="/lessons" element={<LessonsPage />} />
            <Route path="/lessons/:lessonId" element={<LessonDetailPage />} />
            <Route path="/live-lessons" element={<LiveLessonsPage />} />
            <Route path="/live-exams" element={<LiveExamsPage />} />
            <Route
              path="/live-exams/:liveExam"
              element={<LiveExamDetailPage />}
            />
            <Route
              path="/live-exams/:liveExam/results"
              element={<LiveExamResultsPage />}
            />
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/timeline" element={<TimelinePage />} />
            <Route path="/exam-results" element={<ExamResultsPage />} />
            <Route path="/etudes" element={<EtudesPage />} />
            <Route
              path="/etudes/create-request"
              element={<EtudesCreateRequestPage />}
            />
            <Route
              path="/collective-etudes"
              element={<CollectiveEtudesPage />}
            />
            <Route path="/announcements" element={<AnnouncementsPage />} />
            <Route path="/canteen" element={<CanteenPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route
              path="/student-tracking-notes"
              element={<StudentTrackingNotesPage />}
            />
            <Route path="/my-attendance" element={<MyAttendancePage />} />
            <Route
              path="/academic-calendar"
              element={<AcademicCalendarPage />}
            />
            <Route path="/messaging" element={<MessagingPage />} />
            <Route path="/messaging/:id" element={<MessagingDetailPage />} />
            <Route path="/meals" element={<MealsPage />} />
            <Route path="/questions" element={<QuestionsPage />} />
            <Route path="/questions/:questionId" element={<QuestionDetail />} />
            <Route path="/okumanya-app" element={<OkumanyaAppPage />} />
            <Route path="/pdevco-app" element={<PdevcoAppPage />} />
            <Route
              path="/organizational-work"
              element={<OrganizationalWorkPage />}
            />
            <Route path="/work-calendar" element={<WorkCalendarPage />} />
            <Route
              path="/work-calendar/:workCalendarId"
              element={<WorkCalendarDetailPage />}
            />
            <Route path="/tahta-app" element={<TahtaAppPage />} />
            <Route
              path="/tahta-app/register"
              element={<TahtaAppRegisterPage />}
            />
            <Route
              path="/tahta-app/questions"
              element={<TahtaAppQuestionsPage />}
            />
            <Route
              path="/tahta-app/questions/:questionId"
              element={<TahtaAppQuestionDetailPage />}
            />

            {/* Parent Operations - Restricted Route */}
            <Route
              path="/parent-operations"
              element={
                <ProtectedRoute allowedUserTypes={['parent', 'employee']}>
                  <ParentOperationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/parent-operations/vog/create-request"
              element={
                <ProtectedRoute allowedUserTypes={['parent', 'employee']}>
                  <VogCreateRequestPage />
                </ProtectedRoute>
              }
            />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="/not-found" element={<NotFoundPage />} />
        <Route path="*" element={<Navigate to="/not-found" replace />} />
      </Routes>
    </Suspense>
  );
}
